import { query, onSnapshot, where } from 'firebase/firestore'
import {
  bookingsCollGroup
} from '@/firebase-firestore'
import {
  isBefore,
  sub,
  startOfDay,
  endOfDay
} from 'date-fns'
import { bookingConverter } from '@/utils/record-converter'
import { Booking, Product } from '@/types'

export default {
  namespaced: true,
  strict: process.env.NODE_ENV !== 'production',
  state: {
    calendarMinDateTeam: sub(new Date(), { weeks: 2 }),
    calendarMinDateAdmin: sub(new Date(), { weeks: 3 }),
    registerBookings: null,
    registerBookingsListener: null,

    bookings: null,
    bookingsListener: null,

    history: null,
    historyListener: null
  },
  mutations: {
    setRegisterBookings (state: any, arr: []) {
      state.registerBookings = arr
    },
    setRegisterBookingsListener (state: any, listener: number) {
      state.registerBookingsListener = listener
    },
    setBookings (state: any, arr: []) {
      state.bookings = arr
    },
    setBookingsListener (state: any, listener: number) {
      state.bookingsListener = listener
    },
    setHistory (state: any, arr: []) {
      state.history = arr
    },
    setHistoryListener (state: any, listener: number) {
      state.historyListener = listener
    }
  },
  getters: {
    isProductsReady: (state: any, getters: any, rootState: any) => {
      return Array.isArray(rootState.products.products)
    },
    isBookingsReady: (state: any, getters: any) => {
      return Array.isArray(state.bookings) && getters.isProductsReady
    },
    isHistoryReady: (state: any, getters: any) => {
      return Array.isArray(state.history) && getters.isProductsReady
    },
    calendarMinDate: (state: any, getters: any, rootState: any, rootGetters: any) => {
      return rootGetters['user/userAccess'] === 'admin'
        ? state.calendarMinDateAdmin
        : state.calendarMinDateTeam
    },
    registerBookings: (state: any, getters: any, rootState: any, rootGetters: any) => {
      return Array.isArray(state.registerBookings) && getters.isProductsReady
        ? state.registerBookings
          .map((row: Booking) => {
            row.product = rootGetters['products/getProduct'](row.productId)
            return row
          })
        : []
    },
    bookings: (state: any, getters: any, rootState: any, rootGetters: any) => {
      return getters.isBookingsReady
        ? state.bookings
          .map((row: Booking) => {
            row.product = rootGetters['products/getProduct'](row.productId)
            return row
          })
        : []
    },
    history: (state: any, getters: any, rootState: any, rootGetters: any) => {
      return getters.isHistoryReady
        ? state.history
          .map((row: Booking) => {
            row.product = rootGetters['products/getProduct'](row.productId)
            return row
          })
        : []
    }
  },
  actions: {
    releaseRegisterBookings ({ commit, state }: { commit: any, state: any }) {
      if (state.registerBookingsListener) {
        state.registerBookingsListener()
        commit('setRegisterBookingsListener', null)
        commit('setRegisterBookings', null)
        console.warn('Removed listener for register booking')
      }
    },
    getRegisterBookings ({ commit, state, dispatch }: { commit: any, state: any, dispatch: any }, date: Date = new Date()) {
      if (state.registerBookingsListener) {
        dispatch('releaseRegisterBookings')
      }
      console.time('getRegisterBookings')
      const q = query(
        bookingsCollGroup,
        where('dateEnd', '<=', endOfDay(date)),
        where('dateEnd', '>=', startOfDay(date))
      )
      onSnapshot(q, { includeMetadataChanges: true }, async (snapshot) => {
        if (snapshot.metadata.fromCache) {
          return
        }
        if (!state.registerBookings) {
          console.timeEnd('getRegisterBookings')
        }
        const bookings: Booking[] = []
        snapshot.forEach((doc) => {
          const recordRef = doc.ref.parent.parent
          const recordId = recordRef?.id
          if (recordId) {
            bookings.push(bookingConverter.fromFirestore({
              id: doc.id,
              ...doc.data(),
              recordId
            }))
          }
        })
        commit('setRegisterBookings', bookings)
      })
    },
    releaseBookings ({ commit, state }: { commit: any, state: any }) {
      if (state.bookingsListener) {
        state.bookingsListener()
        commit('setBookingsListener', null)
        commit('setBookings', null)
        console.warn('Removed listener for booking')
      }
    },
    getBookings ({ commit, state, dispatch, getters }: { commit: any, state: any, dispatch: any, getters: any }) {
      if (state.bookingsListener) {
        dispatch('releaseBookings')
      }
      console.time('getBookings')
      const q = query(
        bookingsCollGroup,
        where('dateEnd', '>=', getters.calendarMinDate)
      )
      const listener = onSnapshot(q, { includeMetadataChanges: true }, async (snapshot) => {
        if (snapshot.metadata.fromCache) {
          return
        }
        if (!state.bookings) {
          console.timeEnd('getBookings')
        }
        console.time('getBookings.onSnapshot')
        const rows: Booking[] = []
        snapshot.forEach((doc) => {
          const recordRef = doc.ref.parent.parent
          const recordId = recordRef?.id
          if (recordId) {
            rows.push(bookingConverter.fromFirestore({
              id: doc.id,
              ...doc.data(),
              recordId
            }))
          }
        })
        commit('setBookings', rows)
        console.timeEnd('getBookings.onSnapshot')
      })
      commit('setBookingsListener', listener)
    },
    releaseHistory ({ commit, state }: { commit: any, state: any }) {
      if (state.historyListener) {
        state.historyListener()
        commit('setHistoryListener', null)
        commit('setHistory', null)
        console.warn('Removed listener for history')
      }
    },
    getHistory ({ commit, state, dispatch }: { commit: any, state: any, dispatch: any }, interval: Interval) {
      if (state.historyMinDate && !isBefore(interval.start, state.historyMinDate)) {
        return
      }
      if (state.historyListener) {
        dispatch('releaseHistory')
      }
      console.warn('getHistory', interval)
      console.time('getHistory')
      const q = query(
        bookingsCollGroup,
        where('dateEnd', '>=', interval.start),
        where('dateEnd', '<=', interval.end)
      )
      const listener = onSnapshot(q, { includeMetadataChanges: true }, async (snapshot) => {
        if (snapshot.metadata.fromCache) {
          return
        }
        if (!state.history) {
          console.timeEnd('getHistory')
        }
        console.time('getHistory.onSnapshot')
        const rows: Booking[] = []
        snapshot.forEach((doc) => {
          const recordRef = doc.ref.parent.parent
          const recordId = recordRef?.id
          if (recordId) {
            rows.push(bookingConverter.fromFirestore({
              id: doc.id,
              ...doc.data(),
              recordId
            }))
          }
        })
        commit('setHistory', rows)
        console.timeEnd('getHistory.onSnapshot')
      })
      commit('setHistoryListener', listener)
    }
  }
}
